import { useCallback } from "react";

import { UpdateUserAppSettingInput } from "~/gql/graphqlBase.generated";
import snackMessages from "~/helpers/snackMessages";
import useMutationRunner from "~/hooks/useMutationRunner";
import { useUpdateUserAppSettingMutation } from "~/services/endpoints/apiEndpoints";

const ENTITY_LABEL = "Setting";

export interface UpdateUserAppSettingOptions {
	entityLabel?: string;
	silent?: boolean;
}

export interface UseUserAppSettingMutationsReturn {
	isMutating: boolean;
	updateUserAppSetting: (
		input: UpdateUserAppSettingInput,
		options?: UpdateUserAppSettingOptions
	) => Promise<{ message: string } | null>;
}

const useUserAppSettingMutations = (): UseUserAppSettingMutationsReturn => {
	const { run } = useMutationRunner();
	const [updateMutation, { isLoading }] = useUpdateUserAppSettingMutation();

	const updateUserAppSetting = useCallback(
		async (
			input: UpdateUserAppSettingInput,
			options?: UpdateUserAppSettingOptions
		): Promise<{ message: string } | null> => {
			const entityLabel = options?.entityLabel ?? ENTITY_LABEL;
			const ok = await run(
				updateMutation({ input }),
				(data) => data.updateUserAppSetting,
				snackMessages.saveError(entityLabel),
				{ silent: options?.silent }
			);
			return ok ? { message: snackMessages.saveSuccess(entityLabel) } : null;
		},
		[run, updateMutation]
	);

	return { isMutating: isLoading, updateUserAppSetting };
};

export default useUserAppSettingMutations;
